import { Image, Tooltip } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';

import { StateIndicator } from '@texas/components/shared/indicators/StateIndicator';
import { StepsIndicator } from '@texas/components/shared/indicators/StepsIndicator';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { AngularNavLink } from '../shared/AngularNavLink';
import { ExpandImage } from '../shared/image/ExpandImage';
import { Icons } from '../shared/Icons';
import { clientEndpoints } from '@texas/clientEndpoints';
import placeholderImg from '@texas/assets/placeholder-50.png';
import { SearchVariantItem } from '@texas/api/endpoints/search/searchVariantsTypes';
import { ArticleProcess } from '@texas/api/endpoints/variantsApi';
import { ArticleState } from '@texas/api/endpoints/articlesApi';
import { FilteredTagsCell } from '@texas/components/shared/tags/FilteredTagsCell';
import { TagColumnHeader } from '@texas/components/shared/tags/TagColumnHeader';

export const TableColumns = (t: TFunction) => {
  const columnHelper = createColumnHelper<SearchVariantItem>();
  const columns = [
    columnHelper.accessor('imageIdentifier', {
      cell: (info) => (
        <ExpandImage identifier={info.getValue()} variant="scale">
          <Image
            boxSize="12"
            src={
              info.getValue()
                ? clientEndpoints.previewImage(info.getValue(), 98)
                : undefined
            }
            fallbackSrc={placeholderImg}
            objectFit="contain"
          />
        </ExpandImage>
      ),
      header: t('general.photo'),
      enableSorting: false,
      size: 30,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('syncedToErp', {
      cell: (info) =>
        info.getValue() ? (
          <Tooltip label={t('garp.syncedToGarp')}>
            <span>
              <Icons.BriefcaseArrowLeftRight boxSize={4} />
            </span>
          </Tooltip>
        ) : null,
      header: t('garp.syncedToGarp'),
      enableSorting: false,
      size: 60,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('itemCode', {
      cell: (info) => (
        <AngularNavLink
          to="article"
          navobj={{
            articleId: info.row.original.articleId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.itemCode'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('variantName', {
      cell: (info) => (
        <AngularNavLink
          to="variant"
          navobj={{
            articleId: info.row.original.articleId,
            variantId: info.row.original.variantId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('variant.variant'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('customerName', {
      cell: (info) => (
        <AngularNavLink
          to="customer"
          navobj={{
            customerId: info.row.original.customerId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.brand'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('concept', {
      cell: (info) => (
        <AngularNavLink
          to="concept"
          navobj={{
            conceptId: info.row.original.conceptId,
          }}
        >
          {info.getValue()}
        </AngularNavLink>
      ),
      header: t('general.concept'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('name', {
      cell: (info) => info.getValue(),
      header: t('general.description'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('tags', {
      cell: (info) => {
        const tags = info.getValue();
        if (tags.length === 0) return null;
        return <FilteredTagsCell tags={tags} />;
      },
      header: () => <TagColumnHeader />,
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('branch', {
      cell: (info) => info.getValue(),
      header: t('general.branch'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('created', {
      cell: (info) => formatDate(Locale.En, info.getValue()),
      header: t('article.created'),
      enableSorting: true,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('state', {
      cell: (info) => (
        <StateIndicator
          state={
            ArticleState[
              info.getValue() as unknown as keyof typeof ArticleState
            ]
          }
        />
      ),
      header: t('general.state'),
      enableSorting: false,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('articleProcess', {
      cell: (info) =>
        ArticleState[
          info.row.original.state as unknown as keyof typeof ArticleState
        ] == ArticleState.Cancelled ? null : (
          <StepsIndicator
            currentStep={
              ArticleProcess[
                info.getValue() as unknown as keyof typeof ArticleProcess
              ]
            }
          />
        ),
      enableSorting: false,
      header: t('general.articleProcess'),
      meta: { toggleable: true, defaultVisible: true },
    }),
  ];

  return columns;
};
